// @catalog F.010
/**
 * Exporta las filas del diálogo "Productos seleccionados" a CSV.
 *
 * Formato: UTF-8 con BOM (Excel detecta encoding correcto), separador `,`,
 * strings con quotes dobles si contienen separador / quote / newline.
 * Columnas espejan la tabla visible.
 */
import type { SelectedProduct } from "../../components/Forecast/SelectedProducts.types";
import type { CalcResult, SamplesByProduct } from "../../components/Forecast/SelectedProductsDialog";
import { finalLineQty } from "./final-line-qty";

export interface ExportCsvOptions {
  products: SelectedProduct[];
  calcResults: CalcResult[];
  samplesByProduct: SamplesByProduct;
  suggestedOverrides: Record<string, number>;
  suggestedPlusOverrides: Record<string, number>;
  extraByProduct: Record<string, number>;
  months: number;
  growthPct: number;
  globalMarginRatio: number | null;
}

export function csvEscape(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return "";
  const s = String(value);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function getDisplayText(
  text: string | { en_US: string; es_MX: string } | null | undefined,
): string {
  if (!text) return "";
  if (typeof text === "string") return text;
  return text.es_MX || text.en_US || "";
}

// Pack efectivo: misma regla que getCurrentPurchaseCant del dialog
// (user-persisted > original > 1) para que el Excel coincida con la tabla.
function effectivePackSize(p: SelectedProduct): number {
  const base =
    typeof p.purchaseCant === "number" && p.purchaseCant > 0
      ? p.purchaseCant
      : 1;
  const persisted =
    typeof p.purchaseCantUser === "number" && p.purchaseCantUser > 0
      ? p.purchaseCantUser
      : null;
  return persisted ?? base;
}

export function buildSelectedProductsCsv(opts: ExportCsvOptions): string {
  const {
    products,
    calcResults,
    samplesByProduct,
    suggestedOverrides,
    suggestedPlusOverrides,
    extraByProduct,
    months,
    growthPct,
  } = opts;

  const resultById = new Map<string, CalcResult>();
  for (const r of calcResults) resultById.set(r.id, r);

  const headers = [
    "ERP",
    "Metodo",
    "Producto",
    "SKU",
    "Pack",
    "Inventario",
    "Reposicion sugerida",
    "Muestras nuevas",
    "Costo unitario MXN (c/IVA)",
    "Precio venta MXN (c/IVA)",
    "Margen MXN (c/IVA)",
    "Total MXN (c/IVA)",
  ];

  const rows: string[] = [];
  rows.push(headers.map(csvEscape).join(","));

  let grandTotal = 0;
  let grandMargin = 0;

  for (const p of products) {
    const calc = resultById.get(p.unifiedProductId);
    const base = calc?.details?.baseSuggested ?? 0;
    const sample =
      samplesByProduct[p.unifiedProductId]?.by_source?.seeding.total ?? 0;
    const extra = extraByProduct[p.unifiedProductId] ?? 0;
    const purchaseQty = effectivePackSize(p);
    // B5.3 — misma fuente de verdad que pantalla/PO (finalLineQty): el override
    // de REPOSICIÓN ya se reflejaba en el CSV; ahora comparten la cascada exacta.
    const finalQty = finalLineQty({
      baseSuggested: base,
      suggestedOverride: suggestedOverrides[p.unifiedProductId],
      suggestedPlusOverride: suggestedPlusOverrides[p.unifiedProductId],
      sampleQty: sample,
      extraQty: extra,
      packSize: purchaseQty,
    });

    const unitCost =
      typeof (p as { lastPurchaseCost?: number }).lastPurchaseCost === "number"
        ? (p as { lastPurchaseCost: number }).lastPurchaseCost
        : null;
    const sellPrice =
      typeof (p as { sellingPrice?: number }).sellingPrice === "number"
        ? (p as { sellingPrice: number }).sellingPrice
        : null;

    const total = unitCost !== null ? unitCost * finalQty : 0;
    const margin =
      unitCost !== null && sellPrice !== null
        ? (sellPrice - unitCost) * finalQty
        : 0;

    grandTotal += total;
    grandMargin += margin;

    const erp =
      (p as { existsInOdoo16?: boolean }).existsInOdoo16 &&
      (p as { existsInOdoo18?: boolean }).existsInOdoo18
        ? "16/18"
        : (p as { existsInOdoo18?: boolean }).existsInOdoo18
          ? "18"
          : (p as { existsInOdoo16?: boolean }).existsInOdoo16
            ? "16"
            : "—";

    const inventory =
      typeof (p as { totalAvailableQty?: number }).totalAvailableQty === "number"
        ? (p as { totalAvailableQty: number }).totalAvailableQty
        : "";

    rows.push(
      [
        csvEscape(erp),
        csvEscape(calc?.forecastMethod ?? ""),
        csvEscape(getDisplayText(p.unifiedName as never)),
        csvEscape(p.unifiedCode ?? ""),
        csvEscape(purchaseQty),
        csvEscape(inventory),
        csvEscape(finalQty),
        csvEscape(sample),
        csvEscape(unitCost ?? ""),
        csvEscape(sellPrice ?? ""),
        csvEscape(margin.toFixed(2)),
        csvEscape(total.toFixed(2)),
      ].join(","),
    );
  }

  rows.push("");
  rows.push(`# Horizonte,${months} mes${months === 1 ? "" : "es"}`);
  rows.push(`# Productos,${products.length}`);
  rows.push(`# Crecimiento proyectado,${growthPct}%`);
  rows.push(`# Total margen MXN,${grandMargin.toFixed(2)}`);
  rows.push(`# Monto total MXN,${grandTotal.toFixed(2)}`);
  rows.push(`# Generado,${new Date().toISOString()}`);

  return rows.join("\r\n");
}

export function downloadCsv(filename: string, csv: string): void {
  const bom = "﻿";
  const blob = new Blob([bom + csv], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function defaultCsvFilename(prefix = "forecast_compras"): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${prefix}_${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}.csv`;
}
